/**
 * DefaultDetails Component
 * 
 * A React component that provides a standardized display for accommodation details
 * when no specific component exists for the accommodation type. Handles common
 * accommodation information like check-in/out times, duration, and costs.
 * 
 * Features:
 * - Date and time formatting
 * - Currency formatting with localization
 * - Duration calculation
 * - Dynamic detail rendering
 * - URL detection and link creation
 * - Responsive grid layout
 * 
 * Data Structure Expected:
 * accommodation: {
 *   details: {
 *     checkInDate: string (ISO date),
 *     checkOutDate: string (ISO date),
 *     [other dynamic fields]: any
 *   },
 *   cost: {
 *     amount: number,
 *     currency: string,
 *     paid: boolean
 *   }
 * }
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { DetailSection } from '../common';
import { format, parseISO } from 'date-fns';
import { FaCalendarAlt, FaBed } from 'react-icons/fa';

/**
 * Renders the default accommodation details view
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.accommodation - The accommodation data object
 * @param {Object} props.accommodation.details - Detailed information about the accommodation
 * @param {Object} [props.accommodation.cost] - Cost information if available
 * @returns {React.ReactElement} A structured view of accommodation details
 */
const DefaultDetails = ({ accommodation, days, onUnlink }) => {
  const { tripId } = useParams();

  // Early return if accommodation is null or missing
  if (!accommodation) {
    return <div className="error-message">Accommodation not found</div>;
  }

  const { details, cost } = accommodation;

  if (!details) {
    return <div className="error-message">No accommodation details available</div>;
  }

  /**
     * Formats date and time strings for display
     * Handles invalid dates and missing values
     * 
     * @param {string} dateStr - ISO date string to format
     * @returns {Object} Formatted date and time strings
     * @returns {string} .date - Formatted date (e.g., "Jan 1, 2024")
     * @returns {string} .time - Formatted time (e.g., "2:30 PM")
     */
  const formatDateTime = (dateStr) => {
    if (!dateStr) return { date: 'Not specified', time: '' };

    try {
      const date = parseISO(dateStr);
      return {
        date: format(date, 'MMM d, yyyy'),
        time: format(date, 'h:mm a')
      };
    } catch (e) {
      return { date: dateStr, time: '' };
    }
  };

  /**
     * Formats currency values with proper localization
     * Falls back to basic formatting if Intl.NumberFormat fails
     * 
     * @param {number} amount - The amount to format
     * @param {string} [currency='USD'] - The currency code
     * @returns {string} Formatted currency string
     */
  const formatCurrency = (amount, currency = 'USD') => {
    if (amount === undefined || amount === null) return 'Not specified';

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return `${amount} ${currency}`;
    }
  };

  // Process check-in and check-out information
  const checkIn = {
    datetime: formatDateTime(details.checkInDate)
  };

  const checkOut = {
    datetime: formatDateTime(details.checkOutDate)
  };

  /**
     * Calculates the number of nights between check-in and check-out
     * Handles missing dates and invalid date ranges
     * 
     * @returns {number|null} Number of nights or null if calculation not possible
     */
  const calculateNights = () => {
    if (!details.checkInDate || !details.checkOutDate) return null;

    try {
      const checkInDate = new Date(details.checkInDate);
      const checkOutDate = new Date(details.checkOutDate);
      const diffTime = Math.abs(checkOutDate - checkInDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch (e) {
      console.error('Error calculating nights:', e);
      return null;
    }
  };

  const nights = calculateNights();

  return (
    <div className="default-accommodation-details">
      {/* Stay Duration Section */}
      <DetailSection title="Stay Information">
        <div className="details-grid">
          {/* Check-in Information */}
          <div className="detail-item">
            <div className="detail-label">
              <FaCalendarAlt /> Check-in
            </div>
            <div className="detail-value">
              {checkIn.datetime.date}
              {checkIn.datetime.time && (
                <span className="time-display"> at {checkIn.datetime.time}</span>
              )}
            </div>
          </div>

          {/* Check-out Information */}
          <div className="detail-item">
            <div className="detail-label">
              <FaCalendarAlt /> Check-out
            </div>
            <div className="detail-value">
              {checkOut.datetime.date}
              {checkOut.datetime.time && (
                <span className="time-display"> at {checkOut.datetime.time}</span>
              )}
            </div>
          </div>

          {/* Duration Display */}
          {nights && (
            <div className="detail-item">
              <div className="detail-label">
                <FaBed /> Duration
              </div>
              <div className="detail-value">{nights} {nights === 1 ? 'night' : 'nights'}</div>
            </div>
          )}
        </div>

        {/* Related Days Section and Documents removed from here (moved to parent layout) */}
      </DetailSection>

      {/* Additional Details Section */}
      <DetailSection title="Accommodation Details">
        <div className="details-grid">
          {/* Dynamic Details Rendering */}
          {Object.entries(details).map(([key, value]) => {
            // Skip already displayed check-in/out dates
            if (key === 'checkInDate' || key === 'checkOutDate') return null;

            // Skip empty values
            if (value === null || value === undefined || value === '') return null;

            return (
              <div className="detail-item" key={key}>
                <div className="detail-label">
                  {/* Convert camelCase to Title Case with spaces */}
                  {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                </div>
                <div className="detail-value">
                  {/* Convert URLs to clickable links */}
                  {typeof value === 'string' && (value.startsWith('http') || value.startsWith('www')) ? (
                    <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>
                  ) : (
                    value
                  )}
                </div>
              </div>
            );
          })}

          {/* Cost Information Display */}
          {cost && cost.amount > 0 && (
            <div className="detail-item">
              <div className="detail-label">Cost</div>
              <div className="detail-value">
                {formatCurrency(cost.amount, cost.currency)}
                <span className="payment-status">
                  {cost.paid ? ' (Paid)' : ' (Unpaid)'}
                </span>
              </div>
            </div>
          )}
        </div>
      </DetailSection>
    </div>
  );
};

export default DefaultDetails; 